import React, { Component } from "react";
import Select from "react-select";
import { Alert, Card, Col, Container, Row } from "react-bootstrap";
import { Checkbox, Radio, RadioGroup } from "@blueprintjs/core";

import TranscriptBase from "./TranscriptBase";
import Button from "../base-components/button";
import { FAQ_DOCUMENT_URL } from "../../utils/index.js";
import { sendAPIRequest } from "shared/utils/requests";
import SpeakerBubbleWithEventQualityReason from "./SpeakerBubbleWithEventQualityReason";

export const eventRatingTypes = {
  PASS: "PASS",
  EDIT: "EDIT",
  REDACT: "REDACT",
};

export const sortTqrReasonOptionsKeys = (tqrReasonOptions) => {
  return Object.keys(tqrReasonOptions).sort((a, b) =>
    // sort by order_num ascending  
    tqrReasonOptions[a].order_num - tqrReasonOptions[b].order_num
  );
};

/**
 * This is the main component controlling the TQR interaction.
 * @memberof qa-world.components
 * @alias TranscriptQualityRating
 */

class TranscriptQualityRating extends Component {
  constructor(props) {
    super(props);

    this.callDisplayID = props.transcription.call_info.display_id;

    this.flagOptions = this.constructFlagOptions();

    this.tqrReasonOptions = this.props.transcription.tqr_reason_options;
    this.tqrReasonOptionsSortedKeys = sortTqrReasonOptionsKeys(this.tqrReasonOptions);
    this.tqrReasonThreshold = this.props.transcription.tqr_reason_threshold;
    this.tqrMaxRating = this.props.transcription.tqr_max_rating;
    this.maxEventEditsFor_5_Rating = this.props.transcription.max_event_edits_for_5_rating;
    this.autoTranscribedCall = this.props.transcription.call_info.auto_transcriber;

    this.orderedEventIDs = this.constructOrderedEventIDs();
    
    this.tqrReasonOptionSpeakerMergeText = "Speaker Label Error";
    this.tqrReasonOptionSpeakerMergeIds = [];
    Object.keys(this.tqrReasonOptions).forEach(key => {
      if (key.includes(this.tqrReasonOptionSpeakerMergeText)) {
        this.tqrReasonOptionSpeakerMergeIds.push(this.tqrReasonOptions[key].id);
      }
    });

    this.state = {
      blockProps: this.constructSpeakerBubbleWithEventQualityReasonBlockProps(),
      showAlert: this.props.transcription.call_info.auto_transcriber,
      flagValue: undefined,
      tqrValue: undefined,
      tqrValueOfHumanTranscribedEvents: undefined,
      tqrReasonValues: {},
      tqrReasonNote: undefined,
      eventQualityReasons: {},
      testBadTranscript: undefined,
      isSubmitting: false,
      highlightPreviousPasses: false,
      eventsChangedFromPassCount: 0,
    };
  }

  updateHighlightPreviousPasses = value => {
    this.setState({ highlightPreviousPasses: !!value });
  };

  addEventsChangedFromPassCount = value => {
    this.setState(prevState => ({
      eventsChangedFromPassCount: prevState.eventsChangedFromPassCount + value,
    }));
  };

  constructFlagOptions = () => {
    const { transcription } = this.props;

    let flagOptions = [
      {
        label: "None",
        value: null,
      },
    ];
    flagOptions.push(...transcription.flag_reasons);

    return flagOptions;
  };

  constructOrderedEventIDs = () => {
    return this.props.segments.map(segment => segment.metadata.eventId).sort();
  };

  constructSpeakerBubbleWithEventQualityReasonBlockProps = () => {
    const { transcription } = this.props;

    return {
      editReasonOptions: transcription.tqr_reason_options,
      redactReasonOptions: transcription.redact_reason_options,
      propagateQualityReasonCallback: this.updateEventQualityReasons,
      updateHighlightPreviousPasses: this.updateHighlightPreviousPasses,
      addEventsChangedFromPassCount: this.addEventsChangedFromPassCount,
    };
  };

  updateEventQualityReasons = eventQualityReason => {
    this.setState(prevState => ({
      eventQualityReasons: {
        ...prevState.eventQualityReasons,
        [eventQualityReason.eventId]: eventQualityReason,
      },
    }));
  };

  shouldHighlightPreviousPasses = () => {
    return (
      this.props.transcription.prior_call_existed &&
      this.state.eventsChangedFromPassCount > 0 &&
      this.state.highlightPreviousPasses
    );
  };

  composeDataForSubmit = () => {
    const {
      flagValue,
      tqrValue,
      tqrReasonValues,
      tqrReasonNote,
      eventQualityReasons,
      testBadTranscript,
      tqrValueOfHumanTranscribedEvents,
    } = this.state;

    const ret = {
      callDisplayID: this.callDisplayID,
      flagValue: flagValue,
      tqrValue: tqrValue,
      tqrValueOfHumanTranscribedEvents: this.shouldHighlightPreviousPasses()
        ? tqrValueOfHumanTranscribedEvents
        : tqrValue,
      eventQualityReasons: Object.values(eventQualityReasons),
      tqrReasonNote: (tqrReasonNote && tqrReasonNote.trim()) || null,
      tqrReasonValues: Object.values(this.tqrReasonOptions).filter(
        tqrReasonOption => {
          return !!tqrReasonValues[tqrReasonOption.id];
        }
      ).map(tqrReasonOption => { return tqrReasonOption.id; }),
      testBadTranscript: testBadTranscript ?? false,
    };

    if (ret.tqrValue && ret.tqrValue >= this.tqrMaxRating) {
      ret.tqrReasonValues = null;
      ret.tqrReasonNote = null;
    }

    return ret;
  };

  eventIDsWithUnmatchedSpeakerMergeTqr = () => {
    const ret = [];
    
    const eventQualityReasons = Object.values(this.state.eventQualityReasons);

    const eventIDsWithSpeakerMerge = eventQualityReasons.filter((eqr) => {
      return (eqr.ratingType === eventRatingTypes.EDIT) &&
      (this.tqrReasonOptionSpeakerMergeIds.filter(speakerMergeId => eqr.ratingReasonOptionIds.includes(speakerMergeId)).length >= 1)
    }).map((eqr) => {
      return eqr.eventId;
    });

    eventIDsWithSpeakerMerge.forEach((eventID) => {
      const idx = this.orderedEventIDs.indexOf(eventID);
      const prevEventID = this.orderedEventIDs[idx - 1];
      const nextEventID = this.orderedEventIDs[idx + 1];

      const prevEventIsSpeakerMerge = eventIDsWithSpeakerMerge.includes(prevEventID);
      const nextEventIsSpeakerMerge = eventIDsWithSpeakerMerge.includes(nextEventID);
      
      if (!prevEventIsSpeakerMerge && !nextEventIsSpeakerMerge) {
        ret.push(eventID);
      }
    });

    return ret;
  };

  submitIsValid = data => {
    if (!data.tqrValue) {
      window.alert("You must provide a rating for this transcription.");
      return false;
    }

    const hasZeroEdits = null == Object.values(this.state.eventQualityReasons).find(eqr => {
      return eqr.ratingType === eventRatingTypes.EDIT;
    });
    if (data.tqrValue <= 2 && hasZeroEdits) {
      window.alert(`You must mark the appropriate events as ${eventRatingTypes.EDIT} in order to rate this as a ${data.tqrValue}.`);
      return false;
    }

    if (
      this.shouldHighlightPreviousPasses() &&
      !data.tqrValueOfHumanTranscribedEvents
    ) {
      window.alert("You must provide a rating for the highlighted events.");
      return false;
    }

    if (
      data.tqrValue < this.tqrReasonThreshold &&
      !data.tqrReasonValues.length
    ) {
      window.alert("You must select a Rating Reason for this call.");
      return false;
    }

    if (
      this.autoTranscribedCall &&
      data.tqrValue < this.tqrReasonThreshold &&
      data.testBadTranscript == null
    ) {
      window.alert(
        "You must specify 'Yes' or 'No' for 'Send call through for customer quality testing?'."
      );
      return false;
    }

    for (const eventQualityReason of data.eventQualityReasons) {
      if (
        eventQualityReason.ratingType !== eventRatingTypes.PASS &&
        !eventQualityReason.ratingReasonOptionIds.length
      ) {
        window.alert(
          `You must select a reason for each speaker segment ${eventQualityReason.ratingType}.`
        );
        return false;
      }
    }

    if (data.tqrValue === 5) {
      const numEditEvents = data.eventQualityReasons.filter(
        eventQualityReason => {
          return eventQualityReason.ratingType === eventRatingTypes.EDIT;
        }
      ).length;

      if (numEditEvents > this.maxEventEditsFor_5_Rating) {
        // prettier-ignore
        window.alert(
          `Transcripts that are rated ${data.tqrValue} // 'Very Good' can only have ` +
          `${this.maxEventEditsFor_5_Rating} ${eventRatingTypes.EDIT} ` +
          `speaker segments, however there are currently ${numEditEvents}.`
        );
        return false;
      }
    }

    const eventIDsWithUnmatchedSpeakerMergeTqr = this.eventIDsWithUnmatchedSpeakerMergeTqr();
    if (eventIDsWithUnmatchedSpeakerMergeTqr.length) {
      // prettier-ignore
      window.alert(
        `For each event labeled as '${this.tqrReasonOptionSpeakerMergeText}', ` +
        "you must label the appropriate " +
        "neighboring event(s) as the event(s) to be merged with.\n" +
        "These Event IDs do not have a neighboring merge event:\n\n" +
        `${eventIDsWithUnmatchedSpeakerMergeTqr.join("\n")}`
      );

      return false;
    }

    return true;
  };

  onSubmit = async () => {
    const { isSubmitting } = this.state;
    const { history, submitURL } = this.props;
    if (isSubmitting) {
      window.alert("Submit is in progress");
      return null;
    }

    const areYouSure = window.confirm("Are you sure you want to submit?");
    if (!areYouSure) {
      return null;
    }

    const data = this.composeDataForSubmit();

    if (this.submitIsValid(data)) {
      this.setState({ isSubmitting: true });

      sendAPIRequest(submitURL(this), "POST", data)
        .then(() => {
          window.alert("Thank you, your submission has been processed!");
          window.location.href = "/calls";
        })
        .catch(err => {
          const responseMessage =
            err?.response?.headers?.message || "Something went wrong";
          window.alert(responseMessage);
        })
        .then(() => {
          this.setState({ isSubmitting: false });
        });
    }
  };

  renderHeader = () => {
    const { creatingCalibration, transcription } = this.props;
    const transcriberInfo = transcription.transcriber_info;
    const callInfo = transcription.call_info;
    const showAlert = this.state.showAlert;

    return (
      <div>
        {showAlert && !creatingCalibration && (
          <Alert
            onClose={() => this.setState({ showAlert: false })}
            dismissible
          >
            Warning! This call uses our new auto-transcriber model and has not
            been edited by a human. Rate using instructions from Arlen.
          </Alert>
        )}

        <Row>
          <Col xs={12}>
            <Card>
              <Card.Body>
                <h3 className="h2 large">
                  {creatingCalibration
                    ? "Creating Calibration Test"
                    : "Transcript QA"}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card>
              <Card.Body>
                <div className="h4 medium">Transcriber Info</div>

                <p className="transcriptQualityRating-header-child">
                  <b>Join Date:</b>
                  {" " + transcriberInfo.join_date}
                </p>

                <p className="transcriptQualityRating-header-child">
                  <b>Submitted Transcripts:</b>
                  {" " + transcriberInfo.num_submitted_transcripts}
                </p>

                <p className="transcriptQualityRating-header-child">
                  <b>Good Quality Transcripts:</b>
                  {" " + transcriberInfo.num_good_transcripts}
                </p>

                <div className="h4 medium">Call Info</div>

                <p className="transcriptQualityRating-header-child">
                  <b>CID:</b>
                  {" " + callInfo.display_id}
                </p>

                <p className="transcriptQualityRating-header-child">
                  <b>Occurred At:</b>
                  {" " + callInfo.occurred_at}
                </p>

                <p className="transcriptQualityRating-header-child">
                  <b>Requeue Attempt Index:</b>
                  {" " + callInfo.requeue_attempt_index}
                </p>

                <p className="transcriptQualityRating-header-child">
                  <b>Company Name:</b>
                  {" " + callInfo.company_name}
                </p>

                <p className="transcriptQualityRating-header-child">
                  <b>Rep Name:</b>
                  {" " + callInfo.rep_name}
                </p>

                {callInfo.previous_call_display_id && (
                  <p className="transcriptQualityRating-header-child">
                    <b>Previous Attempt:</b>{" "}
                    {
                      <a
                        href={`/calls/${callInfo.previous_call_display_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {callInfo.previous_call_display_id}
                      </a>
                    }
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <br />
      </div>
    );
  };

  renderSidebar = () => {
    const { creatingCalibration } = this.props;
    const {
      flagValue,
      tqrValue,
      tqrReasonValues,
      tqrReasonNote,
      tqrValueOfHumanTranscribedEvents,
      testBadTranscript,
      isSubmitting,
    } = this.state;

    return (
      <Card className="transcriptQualityRating-sidebar">
        <Card.Body>
          <div>
            <div>How well did the transcriber transcribe the call?</div>

            <div className="tqr-rating mt-3">
              <RadioGroup
                selectedValue={tqrValue != null && tqrValue.toString()}
                onChange={e => {
                  this.setState({
                    tqrValue: +e.target.value,
                  });
                }}
              >
                <Radio label="Very Poor" value="1" />
                <Radio label="Somewhat Poor" value="2" />
                <Radio label="Okay" value="3" />
                <Radio label="Good" value="4" />
                <Radio label="Very Good" value="5" />
              </RadioGroup>
            </div>

            {tqrValue && tqrValue < this.tqrMaxRating && (
              <>
                <div className="transcript-control-divider" />
                <div className="tqr-reason mt-3">
                  <p>Rating Reason</p>

                  {this.tqrReasonOptionsSortedKeys.map(tqrReasonLabel => {
                    const tqrReasonId = this.tqrReasonOptions[tqrReasonLabel].id;

                    return (
                      <Checkbox
                        key={tqrReasonId}
                        label={tqrReasonLabel}
                        checked={!!tqrReasonValues[tqrReasonId]}
                        onChange={() => {
                          this.setState(prevState => ({
                            tqrReasonValues: {
                              ...prevState.tqrReasonValues,
                              [tqrReasonId]: !prevState.tqrReasonValues[
                                tqrReasonId
                              ],
                            },
                          }));
                        }}
                      />
                    );
                  })}

                  <p>
                    Additional notes (this will be emailed to the transcriber if
                    the rating is very poor or somewhat poor)
                  </p>

                  <textarea
                    name="tqrReasonNote"
                    className="transcriptQualityRating-reason-note"
                    wrap="soft"
                    onChange={event =>
                      this.setState({ tqrReasonNote: event.target.value })
                    }
                    value={tqrReasonNote}
                  />
                </div>
                {this.autoTranscribedCall &&
                  tqrValue < this.tqrReasonThreshold && (
                    <>
                      <div className="transcript-control-divider" />

                      <p className="mt-3">
                        Send call through for customer quality testing?
                      </p>
                      <RadioGroup
                        selectedValue={
                          testBadTranscript != null &&
                          (testBadTranscript ? "1" : "0")
                        }
                        onChange={e => {
                          this.setState({
                            testBadTranscript: Boolean(+e.target.value),
                          });
                        }}
                      >
                        <Radio label="Yes" value="1" />
                        <Radio label="No" value="0" />
                      </RadioGroup>
                    </>
                  )}
              </>
            )}

            {this.shouldHighlightPreviousPasses() && (
              <>
                <div className="transcript-control-divider" />

                <div className="mt-3">
                  How well did the transcriber transcribe only the highlighted
                  events?
                </div>

                <div className="mt-3">
                  <RadioGroup
                    selectedValue={
                      tqrValueOfHumanTranscribedEvents != null &&
                      tqrValueOfHumanTranscribedEvents.toString()
                    }
                    onChange={e => {
                      this.setState({
                        tqrValueOfHumanTranscribedEvents: +e.target.value,
                      });
                    }}
                  >
                    <Radio label="Very Poor" value="1" />
                    <Radio label="Somewhat Poor" value="2" />
                    <Radio label="Okay" value="3" />
                    <Radio label="Good" value="4" />
                    <Radio label="Very Good" value="5" />
                  </RadioGroup>
                </div>
              </>
            )}

            <div className="transcript-control-divider" />

            <div className="mt-3">
              <a
                className="transcript-button transcript-control-instructions-button"
                href={FAQ_DOCUMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                View the Transcription FAQ
              </a>
            </div>
          </div>

          <div className="transcript-control-divider" />

          <div className="mt-3">
            <p>Flag a problem with this call?</p>
            <div>
              <Select
                className="flag-call"
                instanceId="flag"
                options={this.flagOptions}
                value={this.flagOptions.find(option => {
                  return option.value == flagValue;
                })}
                onChange={option => this.setState({ flagValue: option.value })}
              />
            </div>
          </div>

          <div className="transcript-control-divider" />

          <div style={{ paddingBottom: 100 }}>
            <Button
              className="transcript-submit transcript-submit-full"
              onClick={this.onSubmit}
              text={`Submit ${
                creatingCalibration ? "Calibration Test" : "Transcript QA"
              }`}
            >
              {isSubmitting && (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="sr-only">Loading...</span>
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  render() {
    const { transcription, segments, segmentsRemoved } = this.props;
    const { blockProps } = this.state;
    return (
      <Container fluid>
        {this.renderHeader()}
        <Row>
          <Col lg={8} style={{ marginBottom: "105px" }}>
            <Card>
              <Card.Body>
                <TranscriptBase
                  readOnly={true}
                  highlightPreviousPasses={this.shouldHighlightPreviousPasses()}
                  SpeakerBubbleComponent={SpeakerBubbleWithEventQualityReason}
                  SpeakerBubbleBlockProps={blockProps}
                  transcription={transcription}
                  segments={segments}
                  segmentsRemoved={segmentsRemoved}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4} style={{ marginBottom: "105px" }}>
            {this.renderSidebar()}
          </Col>
        </Row>
      </Container>
    );
  }
}

export default TranscriptQualityRating;
